import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import Navbar from '../components/Navbar';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import PerfilCompleto from '../components/PerfilCompleto';
import '../styles/ProfileSetupPage.css';
import { edadDesde, MENSAJE_MENOR, MENSAJE_REVISION } from '../lib/edad';
import { PROVINCIAS, DEPARTAMENTOS_POR_PROVINCIA, PROVINCIA_DEFECTO } from '../lib/ubicacion';


// Igual que server/lib/perfilOpciones.js (se usan si el servidor no responde)
const OPCIONES_PERFIL = {
  genero: ['Mujer','Hombre','Mujer trans','Hombre trans','No binario','Género fluido','Agénero','Travesti','Otra identidad','Prefiero no decir'],
  orientacion: ['Heterosexual','Gay','Lesbiana','Bisexual','Pansexual','Asexual','Demisexual','Queer','Prefiero no decir'],
  buscando: ['Hombres','Mujeres','Personas no binarias','Todos'],
  busca_relacion: ['Amistad','Compañía','Algo serio','Una relación','Algo casual'],
  contextura: ['Delgada','Atlética','Promedio','Robusta','Con curvas','Algunos kilos de más'],
  color_pelo: ['Negro','Castaño','Rubio','Pelirrojo','Canoso','Teñido / de color','Rapado / sin pelo'],
  color_ojos: ['Marrones','Negros','Miel','Verdes','Azules','Grises'],
  personalidad: ['Extrovertido/a','Introvertido/a','Divertido/a','Romántico/a','Aventurero/a','Tranquilo/a',
    'Hogareño/a','Fiestero/a','Deportista','Intelectual','Creativo/a','Espiritual',
    'Sincero/a','Cariñoso/a','Responsable','Optimista','Independiente','Familiero/a'],
  personalidad_max: 5,
  tiene_hijos: ['No tengo','1','2','3','4','5','6','7','8','9','10','Más de 10','Prefiero no decir'],
  estado_civil: ['Soltero/a','En una relación','Comprometido/a','En unión convivencial','Casado/a','Separado/a','Divorciado/a','Viudo/a','Relación abierta','Es complicado','Prefiero no decir'],
};

// 50 intereses — igual que el backend
const INTERESES_LISTA = [
  '🧉 Mate','🍷 Vino sanjuanino','⛰️ Montañismo','🎵 Música folclórica','💃 Cueca/Peñas',
  '⚽ Fútbol','🚴 Ciclismo','📚 Lectura','🍳 Cocina','🏊 Natación',
  '🎨 Arte','🐕 Mascotas','🌿 Senderismo','🎮 Gaming','✈️ Viajes',
  '🏋️ Gym/Fitness','🎭 Teatro','🌱 Naturaleza','📸 Fotografía','🍕 Gastronomía',
  '🎸 Guitarra','🎤 Canto','🏄 Deportes acuáticos','🧗 Escalada','🎯 Arquería',
  '🎻 Música clásica','🎹 Piano','🎲 Juegos de mesa','🎪 Circo/Magia','🏇 Equitación',
  '🌌 Astronomía','🔬 Ciencia','💻 Tecnología','🤖 Inteligencia Artificial','📱 Redes sociales',
  '🧘 Yoga/Meditación','🛹 Skateboarding','🏈 Fútbol americano','🎾 Tenis','🏓 Ping Pong',
  '🍻 Salir a bares','🌮 Comida rápida','☕ Cafeterías','🎬 Cine','📺 Series/Streaming',
  '🦋 Voluntariado','🐴 Gauchismo','🌻 Jardinería','🧁 Repostería','🚗 Autos/Motos',
];

export default function ProfileSetupPage() {
  const { user, updateProfile, uploadPhoto, deletePhoto, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { toasts, success, error } = useToast();

  // Options fetched from server
  const [options, setOptions] = useState({
    educacion: [],
    estilo_vida: [],
    habitos_tabaco: [],
    habitos_alcohol: [],
    quiere_hijos: [],
    mascotas: [],
    religion: [],
    ...OPCIONES_PERFIL,
  });

  const [form, setForm] = useState({
    name: user.name || '',
    fecha_nacimiento: user.fecha_nacimiento || '',
    provincia: user.provincia || PROVINCIA_DEFECTO,
    departamento: user.departamento === 'Jachal' ? 'Jáchal' : (user.departamento || ''),
    bio: user.bio || '',
    intereses: Array.isArray(user.intereses) ? user.intereses : [],
    genero: user.genero || '',
    buscando: user.buscando || '',
    orientacion: user.orientacion || '',
    busca_relacion: Array.isArray(user.busca_relacion) ? user.busca_relacion : [],
    // Físico y personalidad
    altura: user.altura || '',
    contextura: user.contextura || '',
    color_pelo: user.color_pelo || '',
    color_ojos: user.color_ojos || '',
    personalidad: Array.isArray(user.personalidad) ? user.personalidad : [],
    // Lifestyle
    educacion: user.educacion || '',
    estilo_vida: user.estilo_vida || '',
    habitos_tabaco: user.habitos_tabaco || '',
    habitos_alcohol: user.habitos_alcohol || '',
    tiene_hijos: user.tiene_hijos || '',
    estado_civil: user.estado_civil || '',
    quiere_hijos: user.quiere_hijos || '',
    mascota: user.mascota || '',
    religion: user.religion || '',
    // Teléfono guardado como "264-4629411"
    tel_area: (user.telefono || '').split('-')[0] || '',
    tel_numero: (user.telefono || '').split('-')[1] || '',
  });

  const FOTO_LIMITS = { free: 1, mate: 3, premium: 10 };
  const fotoLimit = FOTO_LIMITS[user?.plan] || 1;

  // fotos: array de paths, inicializado desde user.fotos o foto_path como fallback
  const initFotos = () => {
    if (Array.isArray(user.fotos) && user.fotos.length > 0) return user.fotos;
    if (user.foto_path) return [user.foto_path];
    return [];
  };
  const [fotos, setFotos] = useState(initFotos);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null); // path en borrado
  const [fieldErrors, setFieldErrors] = useState({});
  const [verPerfil, setVerPerfil] = useState(false);

  // ── Load dropdown options from server ────────────────────────────────────
  useEffect(() => {
    api.profileOptions().then(data => {
      setOptions(o => ({ ...o, ...data }));
    }).catch(() => {
      // Fallback defaults if server is unreachable
      setOptions(o => ({
        ...o,
        educacion: ['Primaria','Secundaria','Terciaria/Técnica','Universitaria en curso','Universitaria completa','Posgrado'],
        estilo_vida: ['Tranquilo/Hogareño','Activo/Deportista','Social/Fiestero','Aventurero','Trabajólico','Equilibrado'],
        habitos_tabaco: ['No fumo','Fumo ocasionalmente','Fumo seguido'],
        habitos_alcohol: ['No tomo alcohol','Tomo ocasionalmente','Tomo seguido'],
        quiere_hijos: ['Sí, quiero','No quiero','No sé todavía','Depende de la pareja'],
        mascotas: ['No tengo','Tengo perro/s','Tengo gato/s','Tengo otros'],
        religion: ['Sin religión','Católico/a','Cristiano/a','Judío/a','Musulmán/a','Agnóstico/a','Otro'],
      }));
    });
  }, []);

  // ── Validation ────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!form.name || form.name.trim().length < 2) errs.name = 'Nombre mínimo 2 caracteres.';
    const edad = edadDesde(form.fecha_nacimiento);
    if (edad === null) errs.fecha_nacimiento = 'Ingresá tu fecha de nacimiento.';
    else if (edad < 18) errs.fecha_nacimiento = MENSAJE_MENOR;
    if (!form.departamento) errs.departamento = 'Seleccioná tu departamento.';
    const area = form.tel_area.trim(), numero = form.tel_numero.trim();
    if (!/^[1-9]\d{1,3}$/.test(area) || !/^[1-9]\d{5,7}$/.test(numero) || area.length + numero.length !== 10)
      errs.telefono = 'Ingresá característica y número sin 0 ni 15 (10 dígitos en total). Ej: 264 - 4629411';
    if (form.intereses.length < 5) errs.intereses = 'Seleccioná al menos 5 intereses.';
    if (form.intereses.length > 10) errs.intereses = 'Máximo 10 intereses.';
    if (form.altura && (Number(form.altura) < 120 || Number(form.altura) > 230)) errs.altura = 'Altura entre 120 y 230 cm.';
    return errs;
  };

  // ── Photo upload ──────────────────────────────────────────────────────────
  // Achica la foto del celular antes de subirla (las de cámara pesan varios MB y con datos móviles
  // tardan mucho). Si el navegador no la puede leer (ej. HEIC), se sube la original.
  const achicarFoto = async (file) => {
    try {
      const bmp = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const escala = Math.min(1, 1600 / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(bmp.width * escala);
      canvas.height = Math.round(bmp.height * escala);
      canvas.getContext('2d').drawImage(bmp, 0, 0, canvas.width, canvas.height);
      bmp.close?.();
      const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
      return blob && blob.size < file.size ? new File([blob], 'foto.jpg', { type: 'image/jpeg' }) : file;
    } catch {
      return file;
    }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset input so same file can be re-selected after delete
    e.target.value = '';
    if (file.type && !file.type.startsWith('image/')) { error('Solo se permiten imágenes.'); return; }

    setUploadingPhoto(true);
    try {
      const foto = await achicarFoto(file);
      if (foto.size > 10 * 1024 * 1024) { error('La foto no puede superar 10MB.'); return; }
      const fd = new FormData();
      fd.append('photo', foto);
      // Que el "Subiendo…" nunca quede para siempre si la conexión se corta
      const r = await Promise.race([
        uploadPhoto(fd),
        new Promise((_, reject) => setTimeout(() => reject(new Error('La subida tardó demasiado. Revisá tu conexión e intentá de nuevo.')), 90000)),
      ]);
      setFotos(r.fotos || []);
      if (r.promoFundador) { await refreshUser(); success(`👑 ¡Sos Fundador! Ganaste ${r.promoFundador.dias} días de Premium gratis`); }
      else success('¡Foto guardada! ✅');
    } catch (err) {
      if (err.message === MENSAJE_REVISION) { window.location.href = '/auth?error=revision'; return; }
      error(err.message || 'Error subiendo la foto. Intentá de nuevo.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // ── Photo delete ──────────────────────────────────────────────────────────
  const handleDeletePhoto = async (photoPath) => {
    if (deletingPhoto) return;
    setDeletingPhoto(photoPath);
    try {
      const r = await deletePhoto(photoPath);
      setFotos(r.fotos || []);
      success('Foto eliminada.');
    } catch (err) {
      error(err.message || 'Error eliminando la foto.');
    } finally {
      setDeletingPhoto(null);
    }
  };

  // ── Interests toggle ──────────────────────────────────────────────────────
  const toggleInteres = (item) => {
    setFieldErrors(fe => ({ ...fe, intereses: '' }));
    setForm(f => {
      if (f.intereses.includes(item)) {
        return { ...f, intereses: f.intereses.filter(i => i !== item) };
      }
      if (f.intereses.length >= 10) return f; // max 10
      return { ...f, intereses: [...f.intereses, item] };
    });
  };

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      error('Completá los campos requeridos antes de guardar.');
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      const guardado = await updateProfile({
        name: form.name.trim(),
        fecha_nacimiento: form.fecha_nacimiento,
        provincia: form.provincia,
        departamento: form.departamento,
        genero: form.genero || null,
        buscando: form.buscando || null,
        orientacion: form.orientacion || null,
        busca_relacion: form.busca_relacion,
        altura: form.altura ? Number(form.altura) : null,
        contextura: form.contextura || null,
        color_pelo: form.color_pelo || null,
        color_ojos: form.color_ojos || null,
        personalidad: form.personalidad,
        bio: form.bio || '',
        intereses: form.intereses,
        educacion: form.educacion || null,
        estilo_vida: form.estilo_vida || null,
        habitos_tabaco: form.habitos_tabaco || null,
        habitos_alcohol: form.habitos_alcohol || null,
        tiene_hijos: form.tiene_hijos || null,
        estado_civil: form.estado_civil || null,
        quiere_hijos: form.quiere_hijos || null,
        mascota: form.mascota || null,
        religion: form.religion || null,
        telefono: `${form.tel_area.trim()}-${form.tel_numero.trim()}`,
      });
      await refreshUser();
      if (guardado?.promoFundador) {
        success(`👑 ¡Sos de los primeros! Ganaste ${guardado.promoFundador.dias} días de Premium gratis`);
        setTimeout(() => navigate('/explorar'), 2600);
      } else {
        success('¡Perfil guardado correctamente! ✅');
        setTimeout(() => navigate('/explorar'), 900);
      }
    } catch (err) {
      if (err.message === MENSAJE_MENOR) { window.location.href = '/auth?error=menor'; return; }
      error(err.message || 'Error guardando el perfil. Revisá los campos e intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  // ── Helper: select field ──────────────────────────────────────────────────
  const SelectField = ({ id, label, fieldKey, optionsList, optional = true }) => (
    <div className="form-group">
      <label className="form-label" htmlFor={id}>
        {label}{!optional && <span style={{ color: 'var(--rojo)' }}> *</span>}
      </label>
      <select id={id} className="form-input" value={form[fieldKey]}
        onChange={e => setForm(f => ({ ...f, [fieldKey]: e.target.value }))}>
        <option value="">Seleccioná...</option>
        {optionsList.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );

  // Chips de selección múltiple (qué buscás, personalidad)
  const toggleLista = (fieldKey, item, max) => setForm(f => {
    const actual = f[fieldKey];
    if (actual.includes(item)) return { ...f, [fieldKey]: actual.filter(x => x !== item) };
    if (max && actual.length >= max) return f;
    return { ...f, [fieldKey]: [...actual, item] };
  });

  const ChipsField = ({ fieldKey, optionsList, max }) => (
    <div className="interests-grid">
      {optionsList.map(item => {
        const sel = form[fieldKey].includes(item);
        const lleno = !sel && max && form[fieldKey].length >= max;
        return (
          <button key={item} type="button"
            className={`interest-tag ${sel ? 'selected' : ''} ${lleno ? 'disabled' : ''}`}
            onClick={() => toggleLista(fieldKey, item, max)} disabled={lleno}>
            {item}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="profile-setup">
      <Navbar />
      <ToastContainer toasts={toasts} />
      <div className="setup-container container">
        <div className="setup-header">
          <h1 className="setup-title">Mi perfil</h1>
          <p className="setup-sub">Completá tus datos para conectar con sanjuaninos 💕</p>
        </div>

        <div className="setup-grid">

          {/* ── FOTOS ─────────────────────────────────────────────────── */}
          <div className="setup-card">
            <h2 className="setup-section-title">
              📸 Mis fotos
              <span className="foto-counter">{fotos.length}/{fotoLimit}</span>
            </h2>

            <div className="photos-grid">
              {/* Fotos existentes */}
              {fotos.map((fp, i) => (
                <div key={fp} className="photo-slot photo-slot--filled">
                  <img src={fp} alt={`Foto ${i + 1}`} />
                  {i === 0 && <span className="photo-main-badge">Principal</span>}
                  <button
                    className="photo-delete-btn"
                    onClick={() => handleDeletePhoto(fp)}
                    disabled={!!deletingPhoto}
                    title="Eliminar foto"
                    type="button">
                    {deletingPhoto === fp ? '⏳' : '×'}
                  </button>
                </div>
              ))}

              {/* Slot para agregar (si no se alcanzó el límite) o, con 1 sola foto permitida, para cambiarla */}
              {(fotos.length < fotoLimit || fotoLimit === 1) && (
                <label
                  className={`photo-slot photo-slot--empty${uploadingPhoto ? ' photo-slot--uploading' : ''}`}
                  title={fotos.length < fotoLimit ? 'Agregar foto' : 'Cambiar foto'}>
                  <span className="photo-add-icon">{uploadingPhoto ? '⏳' : (fotos.length < fotoLimit ? '+' : '🔄')}</span>
                  <span className="photo-add-label">{uploadingPhoto ? 'Subiendo…' : (fotos.length < fotoLimit ? 'Agregar' : 'Cambiar')}</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAddPhoto}
                    hidden
                    disabled={uploadingPhoto}
                  />
                </label>
              )}
            </div>

            <p className="photo-hint">JPG, PNG o WebP · Máx. 10MB por foto</p>
            {user?.plan === 'free' && (
              <p className="photo-hint photo-plan-hint">
                Plan gratuito: 1 foto · <a href="/planes" className="link-rojo">Actualizá para más</a>
              </p>
            )}
            {user?.plan === 'mate' && (
              <p className="photo-hint photo-plan-hint">Plan Mate: hasta 3 fotos 📷</p>
            )}
            {user?.plan === 'premium' && (
              <p className="photo-hint photo-plan-hint">Plan Premium: hasta 10 fotos ⭐</p>
            )}
          </div>

          {/* ── INFORMACIÓN BÁSICA ────────────────────────────────────── */}
          <div className="setup-card">
            <h2 className="setup-section-title">📋 Información básica</h2>
            <div className="setup-fields">

              <div className="form-group">
                <label className="form-label" htmlFor="name">
                  Nombre <span style={{ color: 'var(--rojo)' }}>*</span>
                </label>
                <input id="name" className={`form-input ${fieldErrors.name ? 'input-error' : ''}`}
                  type="text" placeholder="Ej: Valentina Rodríguez"
                  value={form.name} maxLength={60}
                  onChange={e => { setForm(f => ({ ...f, name: e.target.value })); setFieldErrors(fe => ({ ...fe, name: '' })); }} />
                {fieldErrors.name && <span className="form-error">{fieldErrors.name}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="fecha_nacimiento">
                  Fecha de nacimiento <span style={{ color: 'var(--rojo)' }}>*</span>
                </label>
                <input id="fecha_nacimiento" className={`form-input ${fieldErrors.fecha_nacimiento ? 'input-error' : ''}`}
                  type="date" min="1915-01-01" max={new Date().toISOString().slice(0, 10)}
                  value={form.fecha_nacimiento}
                  onChange={e => { setForm(f => ({ ...f, fecha_nacimiento: e.target.value })); setFieldErrors(fe => ({ ...fe, fecha_nacimiento: '' })); }} />
                {edadDesde(form.fecha_nacimiento) >= 18 && <span className="label-hint">Tenés {edadDesde(form.fecha_nacimiento)} años. En tu perfil se muestra solo la edad.</span>}
                {fieldErrors.fecha_nacimiento && <span className="form-error">{fieldErrors.fecha_nacimiento}</span>}
              </div>

              <SelectField id="genero" label="Género" fieldKey="genero" optionsList={options.genero} />
              <SelectField id="orientacion" label="Orientación sexual" fieldKey="orientacion" optionsList={options.orientacion} />
              <SelectField id="buscando" label="Me interesan" fieldKey="buscando" optionsList={options.buscando} />

              <div className="form-group">
                <label className="form-label">¿Qué buscás? <span className="label-hint">(podés elegir varias)</span></label>
                <ChipsField fieldKey="busca_relacion" optionsList={options.busca_relacion} />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="provincia">
                  Provincia <span style={{ color: 'var(--rojo)' }}>*</span>
                </label>
                <select id="provincia" className="form-input"
                  value={form.provincia}
                  onChange={e => { const provincia = e.target.value; setForm(f => ({ ...f, provincia, departamento: '' })); }}>
                  {PROVINCIAS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="departamento">
                  Departamento <span style={{ color: 'var(--rojo)' }}>*</span>
                </label>
                <select id="departamento" className={`form-input ${fieldErrors.departamento ? 'input-error' : ''}`}
                  value={form.departamento}
                  onChange={e => { setForm(f => ({ ...f, departamento: e.target.value })); setFieldErrors(fe => ({ ...fe, departamento: '' })); }}>
                  <option value="">Seleccioná tu departamento...</option>
                  {(DEPARTAMENTOS_POR_PROVINCIA[form.provincia] || []).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
                {fieldErrors.departamento && <span className="form-error">{fieldErrors.departamento}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="tel_area">
                  Teléfono <span style={{ color: 'var(--rojo)' }}>*</span>
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input id="tel_area" className={`form-input ${fieldErrors.telefono ? 'input-error' : ''}`}
                    style={{ width: '6.5em' }} type="tel" inputMode="numeric" maxLength={4} placeholder="264"
                    aria-label="Característica sin 0"
                    value={form.tel_area}
                    onChange={e => { setForm(f => ({ ...f, tel_area: e.target.value.replace(/\D/g, '') })); setFieldErrors(fe => ({ ...fe, telefono: '' })); }} />
                  <span aria-hidden="true">-</span>
                  <input id="tel_numero" className={`form-input ${fieldErrors.telefono ? 'input-error' : ''}`}
                    style={{ flex: 1 }} type="tel" inputMode="numeric" maxLength={8} placeholder="4629411"
                    aria-label="Número sin 15"
                    value={form.tel_numero}
                    onChange={e => { setForm(f => ({ ...f, tel_numero: e.target.value.replace(/\D/g, '') })); setFieldErrors(fe => ({ ...fe, telefono: '' })); }} />
                </div>
                <span className="label-hint">Sin el 0 y sin el 15. No se muestra a otros usuarios.</span>
                {fieldErrors.telefono && <span className="form-error">{fieldErrors.telefono}</span>}
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="bio">
                  Sobre vos <span className="label-hint">({form.bio.length}/300)</span>
                </label>
                <textarea id="bio" className="form-input bio-input"
                  placeholder="¿Qué hacés en San Juan? ¿qué te apasiona? ¿qué buscás?"
                  value={form.bio} maxLength={300} rows={4}
                  onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
              </div>
            </div>
          </div>

          {/* ── CÓMO SOS ──────────────────────────────────────────────── */}
          <div className="setup-card">
            <h2 className="setup-section-title">🪞 Cómo sos <span className="label-optional">(opcional)</span></h2>
            <div className="setup-fields">
              <div className="form-group">
                <label className="form-label" htmlFor="altura">Altura (cm)</label>
                <input id="altura" className={`form-input ${fieldErrors.altura ? 'input-error' : ''}`}
                  type="number" inputMode="numeric" min="120" max="230" placeholder="Ej: 170"
                  value={form.altura}
                  onChange={e => { setForm(f => ({ ...f, altura: e.target.value.replace(/\D/g, '') })); setFieldErrors(fe => ({ ...fe, altura: '' })); }} />
                {fieldErrors.altura && <span className="form-error">{fieldErrors.altura}</span>}
              </div>
              <SelectField id="contextura" label="Contextura" fieldKey="contextura" optionsList={options.contextura} />
              <SelectField id="color_pelo" label="Pelo" fieldKey="color_pelo" optionsList={options.color_pelo} />
              <SelectField id="color_ojos" label="Ojos" fieldKey="color_ojos" optionsList={options.color_ojos} />
              <div className="form-group">
                <label className="form-label">
                  Personalidad <span className="label-hint">({form.personalidad.length}/{options.personalidad_max || 5})</span>
                </label>
                <ChipsField fieldKey="personalidad" optionsList={options.personalidad} max={options.personalidad_max || 5} />
              </div>
            </div>
          </div>

          {/* ── ESTILO DE VIDA ────────────────────────────────────────── */}
          <div className="setup-card">
            <h2 className="setup-section-title">🌟 Estilo de vida <span className="label-optional">(opcional)</span></h2>
            <p className="setup-hint-text">Completá más info para conseguir más conexiones.</p>
            <div className="setup-fields">
              <SelectField id="educacion" label="Nivel educativo" fieldKey="educacion" optionsList={options.educacion} />
              <SelectField id="estilo_vida" label="Estilo de vida" fieldKey="estilo_vida" optionsList={options.estilo_vida} />
              <SelectField id="habitos_tabaco" label="Tabaco" fieldKey="habitos_tabaco" optionsList={options.habitos_tabaco} />
              <SelectField id="habitos_alcohol" label="Alcohol" fieldKey="habitos_alcohol" optionsList={options.habitos_alcohol} />
            </div>
          </div>

          {/* ── FAMILIA Y VALORES ─────────────────────────────────────── */}
          <div className="setup-card">
            <h2 className="setup-section-title">👨‍👩‍👧 Familia y valores <span className="label-optional">(opcional)</span></h2>
            <div className="setup-fields">
              <SelectField id="estado_civil" label="Estado civil" fieldKey="estado_civil" optionsList={options.estado_civil} />
              <SelectField id="tiene_hijos" label="Hijos" fieldKey="tiene_hijos" optionsList={options.tiene_hijos} />
              <SelectField id="quiere_hijos" label="¿Querés hijos?" fieldKey="quiere_hijos" optionsList={options.quiere_hijos} />
              <SelectField id="mascota" label="Mascotas" fieldKey="mascota" optionsList={options.mascotas} />
              <SelectField id="religion" label="Religión" fieldKey="religion" optionsList={options.religion} />
            </div>
          </div>

          {/* ── INTERESES ────────────────────────────────────────────── */}
          <div className="setup-card setup-card-full">
            <h2 className="setup-section-title">
              ❤️ Intereses <span style={{ color: 'var(--rojo)' }}>*</span>
              <span className="interests-count">
                {form.intereses.length}/10 seleccionados
                {form.intereses.length < 5 && (
                  <span style={{ color: 'var(--rojo)', marginLeft: 8 }}>
                    — necesitás {5 - form.intereses.length} más
                  </span>
                )}
              </span>
            </h2>
            <p className="interests-hint">
              Seleccioná entre 5 y 10 intereses para aparecer en los resultados.
              {' '}<strong>Mínimo 5 requeridos.</strong>
            </p>
            {fieldErrors.intereses && (
              <p style={{ color: 'var(--rojo)', fontSize: 13, marginBottom: 12, fontWeight: 600 }}>
                ⚠️ {fieldErrors.intereses}
              </p>
            )}
            <div className="interests-grid">
              {INTERESES_LISTA.map(item => (
                <button key={item} type="button"
                  className={`interest-tag ${form.intereses.includes(item) ? 'selected' : ''} ${
                    !form.intereses.includes(item) && form.intereses.length >= 10 ? 'disabled' : ''
                  }`}
                  onClick={() => toggleInteres(item)}
                  disabled={!form.intereses.includes(item) && form.intereses.length >= 10}>
                  {item}
                </button>
              ))}
            </div>
          </div>

        </div>

        <div className="setup-actions">
          {Object.keys(fieldErrors).length > 0 && (
            <p style={{ color: 'var(--rojo)', fontSize: 14, marginBottom: 12, textAlign: 'center', fontWeight: 600 }}>
              ⚠️ Completá los campos marcados con * para guardar
            </p>
          )}
          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={loading || uploadingPhoto}>
            {loading ? '⏳ Guardando...' : '✅ Guardar y explorar'}
          </button>
          <button className="btn btn-outline" style={{ display: 'flex', margin: '10px auto 0' }} onClick={() => setVerPerfil(true)}>
            👁 Ver cómo ven mi perfil
          </button>
        </div>
        {verPerfil && (
          <PerfilCompleto
            profile={{
              ...user, ...form, fotos,
              foto_path: fotos[0] || null,
              age: edadDesde(form.fecha_nacimiento),
              altura: form.altura ? Number(form.altura) : null,
              afinidad: null, telefono: undefined,
            }}
            onClose={() => setVerPerfil(false)}
          />
        )}
      </div>
    </div>
  );
}
